// v0.4.47 — Per-country VAT-return page (orchestrator).
//
// Renders a single country's local-return form. Sub-components live
// in components/country_return/* (split in the Phase-3 refactor to
// keep this page under the 500-LOC frontend cap):
//   - Box, TypedInEditor, ClarificationCTA
//   - ActionRequiredBanner, MarketplaceFooter
//   - labels (section names + display helpers)
//
// Strict ready-to-file gate: if any reconciliation has failed the
// page surfaces a red banner and refuses the "Mark ready" action.
import React, { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import { Header } from "../components/Header";
import { CountryReturnTabs } from "../components/CountryReturnTabs";
import { Box } from "../components/country_return/Box";
import HeldForAnswerBanner from "../components/country_return/HeldForAnswerBanner";
import { UnplacedRowsNote } from "../components/country_return/UnplacedRowsNote";   // A2-637 Owner D
import { ActionRequiredBanner } from "../components/country_return/ActionRequiredBanner";
import { AdvisoryNotices } from "../components/country_return/AdvisoryNotices";
import { MarketplaceFooter } from "../components/country_return/MarketplaceFooter";
import { LipeChainStatusStrip } from "../components/country_return/LipeChainStatusStrip";
import { SeXmlDownload } from "../components/country_return/SeXmlDownload";
import { ItXmlDownload } from "../components/country_return/ItXmlDownload";
import { FiledSnapshotNote } from "../components/country_return/FiledSnapshotNote";
import { AuthorityCheckPanel } from "../components/country_return/AuthorityCheckPanel";
import { CorrectionPanel } from "../components/country_return/CorrectionPanel";
import { PurchaseRows } from "../components/country_return/PurchaseRows";
import { CopyToFormBar } from "../components/country_return/CopyToFormBar";
import { isFigureToCopy } from "../components/country_return/copyFigure";
import { boxPlace, sharedNotes } from "../components/country_return/boxView";   // A2-638
import { FoldedBoxes, SharedNote } from "../components/country_return/FoldedBoxes";
import { SECTION_LABELS, humanizeSection } from "../components/country_return/labels";
import { getLipeChainAudit, updateClient } from "../lib/api";
import { getPageBuildErrorCopy } from "../lib/ruleCopy";
import { loadCountryReturn } from "./country_return/loadCountryReturn";
import { useTranslation } from "react-i18next";
import { formSectionOrder } from "../lib/formSectionOrder";
import { countryName } from "../lib/countries";
import { CalcCheckBlocker } from "../components/country_return/CalcCheckBlocker";   // A2-535
import { PeriodCadenceNote } from "../components/country_return/PeriodCadenceNote";
import { InvoicedVatNote } from "../components/country_return/InvoicedVatNote";   // A2-540
import { useAuth } from "../auth/AuthContext";

export default function CountryReturn() {
  const { t } = useTranslation();
  // A2-581 lead 29: the purchase table, the correction panel and the
  // authority check are ADMIN routes on the server; an operator saw an
  // error on each of them on every form.
  const isAdmin = (useAuth() || {}).admin?.role === "ADMIN";
  const { calcId, country } = useParams();
  const nav = useNavigate();
  const location = useLocation();
  const upperCountry = (country || "").toUpperCase();
  const [summary, setSummary] = useState(null);
  const [payload, setPayload] = useState(null);
  // v0.5.7 — deep-link + highlight target. The dashboard readiness
  // strip links here as `#box-<id>`; we scroll that box into view and
  // pulse it so "click Deductible input VAT → land on the right box"
  // actually works (was dumping the operator at the page top).
  const [highlightBoxId, setHighlightBoxId] = useState(null);
  const scrollToBox = useCallback((boxId) => {
    const el = document.getElementById(`box-${boxId}`);
    if (!el) return;
    el.scrollIntoView({ behavior: "smooth", block: "center" });
    setHighlightBoxId(boxId);
    window.setTimeout(
      () => setHighlightBoxId((cur) => (cur === boxId ? null : cur)),
      2800,
    );
  }, []);
  // `error` is stamped with the URL it was raised for. If the
  // user navigates away (new calcId/country in the URL), we ignore
  // the stale error in the render layer instead of clearing state
  // in the effect (avoids react-hooks/set-state-in-effect lint).
  const [error, setError] = useState(null);
  const errorForUrl = error && error.calcId === calcId
    && error.country === upperCountry
    ? error : null;
  // v0.4.85 — page-build errors get the same plain-English-first
  // treatment as reconciliation failures (sign-off 2026-06-19,
  // option c). `getPageBuildErrorCopy` returns null when the
  // error code isn't a known page-build error — caller falls
  // back to the raw backend message.
  // v0.4.86 — ctx now includes client_id + country so the
  // "Switch cadence" action can call updateClient inline rather
  // than dropping the operator on /admin/clients to find the
  // right row themselves.
  const errorCopy = errorForUrl
    ? getPageBuildErrorCopy(
        errorForUrl.code, errorForUrl.message,
        {
          calcId,
          clientId: errorForUrl.client_id,
          country: errorForUrl.country || upperCountry,
        },
      )
    : null;
  const errorMessage = errorForUrl ? errorForUrl.message : null;
  // v0.4.86 — busy/error state for the inline api-call action.
  const [apiActionBusy, setApiActionBusy] = useState(null);
  const [apiActionError, setApiActionError] = useState(null);

  const refresh = useCallback(async () => {
    setError(null);
    await loadCountryReturn({
      calcId, upperCountry, nav,
      setSummary, setPayload, setError,
    });
  }, [calcId, upperCountry, nav]);

  const fetchPayload = useCallback(async () => {
    await loadCountryReturn({
      calcId, upperCountry, nav,
      setSummary, setPayload, setError,
    });
  }, [calcId, upperCountry, nav]);

  useEffect(() => {
    const cancelledRef = { current: false };
    loadCountryReturn({
      calcId, upperCountry, nav,
      setSummary, setPayload, setError,
      cancelledRef,
    });
    return () => { cancelledRef.current = true; };
  }, [calcId, upperCountry, nav]);

  // v0.4.48 — guard against stale renders. Until the fetch for the
  // CURRENT url resolves, render as if payload were null. Prevents
  // the prior country's form from showing during the navigation
  // window AND after a 409 (catch sets payload=null, this just
  // belt-and-braces if the catch is skipped for any reason).
  const livePayload = (payload && payload.country === upperCountry)
    ? payload
    : null;

  const bySection = (sec) =>
    (activePayload?.boxes || []).filter(
      (b) => (b.section || "_default") === sec,
    );

  // v0.4.63 — derive section order from the payload itself so any
  // country form renders (ES, IT, and any future ones) without the
  // frontend hardcoding a UK/FR/DE-only list. Preserves first-seen
  // order from the backend, so spec owners control box ordering at
  // form definition time.
  // v0.4.71d — multi-module bundle detection. When the backend
  // returns a bundle payload (IT LIPE monthly), the shape carries
  // `modules: [3 payloads]` + `chain: {...}`. The page switches
  // into module-tab mode and the "active payload" becomes the
  // selected module's individual payload.
  const isBundle = Array.isArray(livePayload?.modules) && livePayload.modules.length === 3;
  const [activeModule, setActiveModule] = useState(0);
  const [onlyCopy, setOnlyCopy] = useState(false);   // A2-478: only the boxes to copy onto the form
  // A2-638 — the folded "not used this period" and "workings" lines
  const [openFold, setOpenFold] = useState({ unused: false, stub: false, working: false, optional: false });
  const [chainAuditOpen, setChainAuditOpen] = useState(false);
  const [chainAuditRecords, setChainAuditRecords] = useState(null);
  const showChainAudit = async () => {
    setChainAuditOpen(true);
    try {
      const out = await getLipeChainAudit(calcId);
      setChainAuditRecords(out.chain_audit || []);
    } catch {
      setChainAuditRecords([]);
    }
  };
  const activePayload = isBundle ? livePayload.modules[activeModule] : livePayload;
  // A2-581 lead 26: "go to box" buttons scrolled only to a box already on
  // screen, so with "Show only the figures to copy" on, or the box in
  // another month's tab of an Italian bundle, they did nothing. The box is
  // shown first, then scrolled to.
  const revealBox = useCallback((boxId) => {
    if (document.getElementById(`box-${boxId}`)) { scrollToBox(boxId); return; }
    setOnlyCopy(false);
    setOpenFold({ unused: true, stub: true, working: true, optional: true });   // A2-638: a folded box is drawn only when open
    // every month of an Italian bundle has the same box ids: stay on the
    // month on screen when it holds the box (review of 27a0aacf)
    const has = (m) => (m?.boxes || []).some((b) => String(b.box_id) === String(boxId));
    if (isBundle && !has(livePayload.modules[activeModule])) {
      const i = livePayload.modules.findIndex(has);
      if (i >= 0) setActiveModule(i);
    }
    window.setTimeout(() => scrollToBox(boxId), 120);
  }, [scrollToBox, isBundle, livePayload, activeModule]);
  useEffect(() => {
    const on = (e) => { if (e.detail?.boxId) revealBox(e.detail.boxId); };
    window.addEventListener("cr-reveal-box", on);
    return () => window.removeEventListener("cr-reveal-box", on);
  }, [revealBox]);

  // v0.4.63 — section order comes from the payload, no per-country list
  // (lib/formSectionOrder.js). A blank array silently renders no boxes.
  const sectionOrder = formSectionOrder(activePayload?.boxes);

  // v0.5.7 — needs-input gate for THIS country, from the single source
  // (the summary chip's needs_input_pending). The page must never claim
  // "Ready to file" while a gated manual-input box is unentered — that
  // was the contradiction against the dashboard readiness strip.
  const currentChip = (summary?.countries || []).find(
    (c) => c.country === upperCountry,
  );
  const pendingBoxes = currentChip?.needs_input_pending || [];

  const handledHash = useRef(null);
  // v0.5.7 — honour a `#box-<id>` hash (deep-link from the readiness
  // strip): scroll to + pulse the box once the payload has rendered.
  useEffect(() => {
    if (!livePayload) return undefined;
    const m = (location.hash || "").match(/^#box-(.+)$/);
    if (!m) return undefined;
    const boxId = decodeURIComponent(m[1]);
    // A2-638: revealBox opens a fold the box sits in. Once per link, not on
    // every refresh: the hash stays in the URL, and re-revealing after each
    // save turned the operator's "only the figures to copy" filter off and
    // reopened folds they had closed (review of 895e099a).
    const key = `${calcId}|${upperCountry}|${location.hash}`;
    if (handledHash.current === key) return undefined;
    const tmo = window.setTimeout(() => { handledHash.current = key; revealBox(boxId); }, 250);
    return () => window.clearTimeout(tmo);
  }, [livePayload, activeModule, location.hash, revealBox, calcId, upperCountry]);

  return (
    <div className="min-h-screen bg-white dark:bg-slate-900 text-slate-900 dark:text-slate-50">
      <Header />
      <main className="mx-auto max-w-5xl px-6 py-8">
        {/* v0.4.47c — breadcrumb back-nav. Every page must be
            navigable: back to the calc, back to history, back to
            dashboard. */}
        <nav data-testid="country-return-breadcrumb" className="flex items-center gap-2 text-xs tracking-normal text-slate-500 dark:text-slate-400 mb-4">
          <button
            data-testid="back-to-calc"
            onClick={() => nav(location.state?.from || `/?calc_id=${calcId}`)}
            className="text-teal-700 dark:text-teal-300 hover:text-teal-700 dark:hover:text-teal-300 underline"
          >
            {t("ui.country_return.page.back_to_calc", { defaultValue: "← Back to calc" })}
          </button>
          <span className="text-slate-500 dark:text-slate-400">/</span>
          <button
            data-testid="back-to-dashboard"
            onClick={() => nav("/")}
            className="hover:text-slate-500 dark:text-slate-400"
          >
            {t("ui.country_return.page.dashboard", { defaultValue: "Dashboard" })}
          </button>
          <span className="text-slate-500 dark:text-slate-400">/</span>
          <span className="text-slate-600 dark:text-slate-300">{t("ui.country_return.page.breadcrumb_return", { country: upperCountry, defaultValue: `${upperCountry} return` })}</span>
        </nav>
        <div className="mb-6">
          <div className="text-xs font-medium text-teal-700 dark:text-teal-300 mb-1">
            {t("ui.country_return.page.eyebrow", { defaultValue: "Country VAT return" })}
          </div>
          <h1 className="text-2xl font-semibold tracking-tight text-slate-900 dark:text-slate-50">
            {/* A2-537 U6: never a bare country code, even before the form loads */}
            {livePayload?.form_name || activePayload?.form_name || t("ui.country_return.page.heading_fallback", { country: countryName(upperCountry), defaultValue: `${countryName(upperCountry)} VAT return` })}
          </h1>
          <div className="text-sm text-slate-600 dark:text-slate-300 mt-1">
            {livePayload
              ? <>{livePayload.period_start} → {livePayload.period_end} {t("ui.country_return.page.calc_dot", { defaultValue: "· calc" })} <span className="tabular-nums">{calcId}</span></>
              : <>{t("ui.country_return.page.calc", { defaultValue: "calc" })} <span className="tabular-nums">{calcId}</span></>}
          </div>
          <FiledSnapshotNote frozen={!!livePayload?.frozen} snapshot={livePayload?.filed_snapshot}
            replacedBy={livePayload?.replaced_by_calc_id || null} country={upperCountry} />
          {upperCountry === "SE" && livePayload && (
            <SeXmlDownload
              calcId={calcId}
              frozen={!!livePayload.frozen}
              readyToFile={!!livePayload.ready_to_file}
            />
          )}
          {upperCountry === "IT" && livePayload && (
            <ItXmlDownload
              calcId={calcId}
              frozen={!!livePayload.frozen}
              readyToFile={!!livePayload.ready_to_file}
            />
          )}
        </div>

        {summary && (
          <CountryReturnTabs
            calcId={calcId} countries={summary.countries} active={upperCountry}
          />
        )}

        {errorForUrl && (
          <div
            data-testid="country-return-error"
            data-error-code={errorForUrl.code || ""}
            className="text-sm bg-rose-50 dark:bg-rose-950/40 border border-rose-300 dark:border-rose-500 rounded-lg p-4 mb-3"
          >
            {errorCopy ? (
              <>
                <div className="text-rose-900 dark:text-rose-100 font-semibold mb-1">
                  {/* i18n-ignore */}⚠ {errorCopy.headline}
                </div>
                {errorCopy.fix && (
                  <div className="text-xs text-rose-700 dark:text-rose-300 mb-2 leading-snug">
                    {errorCopy.fix}
                  </div>
                )}
                {errorCopy.actions && errorCopy.actions.length > 0 && (
                  <div
                    data-testid="page-build-error-actions"
                    className="flex flex-wrap gap-2 mt-2"
                  >
                    {errorCopy.actions.map((a, i) => {
                      const isApi = a.kind === "api-call";
                      const isAdvisory = a.kind === "advisory" || (!isApi && !a.target);
                      const busy = apiActionBusy === a.id;
                      const onClick = async () => {
                        if (a.kind === "navigate" && a.target) {
                          nav(a.target);
                          return;
                        }
                        if (isApi && a.apiCall) {
                          setApiActionBusy(a.id);
                          setApiActionError(null);
                          try {
                            if (a.apiCall.type === "updateClient.localReturnFrequency") {
                              await updateClient(a.apiCall.clientId, {
                                local_return_frequency: {
                                  [a.apiCall.country]: a.apiCall.cadence,
                                },
                              });
                              await refresh();
                            }
                          } catch (apiErr) {
                            setApiActionError(
                              apiErr?.response?.data?.detail?.message
                              || apiErr?.message
                              || "Action failed",
                            );
                          }
                          setApiActionBusy(null);
                        }
                      };
                      return (
                        <button
                          key={a.id || i}
                          type="button"
                          data-testid={`page-build-error-action-${a.id}`}
                          data-resolution-class="self-resolve"
                          data-resolution-endpoint="navigation"
                          onClick={onClick}
                          disabled={isAdvisory || busy}
                          className={`text-xs font-semibold px-3 py-1.5 rounded-md ${
                            i === 0
                              ? "bg-rose-700 hover:bg-rose-600 text-white"
                              : "border border-rose-400 dark:border-rose-600 text-rose-700 dark:text-rose-200 hover:bg-rose-100 dark:hover:bg-rose-900/40"
                          } ${
                            isAdvisory ? "opacity-60 cursor-default" : ""
                          } ${busy ? "opacity-50 cursor-wait" : ""}`}
                        >
                          {busy ? t("ui.country_return.page.applying", { defaultValue: "Applying…" }) : a.label}
                        </button>
                      );
                    })}
                  </div>
                )}
                {apiActionError && (
                  <div
                    data-testid="page-build-error-action-error"
                    className="text-xs text-rose-700 dark:text-rose-300 mt-2"
                  >
                    {apiActionError}
                  </div>
                )}
                {errorCopy.technicalDetails && (
                  <details className="mt-2 text-[11px] text-rose-700 dark:text-rose-300">
                    <summary className="cursor-pointer hover:underline select-none">
                      ▸ {t("ui.country_return.page.tech_details", { defaultValue: "Technical details (auditor view)" })}
                    </summary>
                    <div className="mt-1 leading-snug">
                      {errorCopy.technicalDetails}
                    </div>
                    <div className="mt-1 leading-snug text-rose-600 dark:text-rose-400">
                      {t("ui.country_return.page.engine_message", { message: errorMessage, defaultValue: `Engine message: ${errorMessage}` })}
                    </div>
                  </details>
                )}
              </>
            ) : (
              <div className="text-rose-700 dark:text-rose-300">
                {errorMessage}
              </div>
            )}
          </div>
        )}

        {isBundle && (
          <LipeChainStatusStrip
            modules={livePayload.modules}
            chain={{ ...livePayload.chain, warnings: livePayload.warnings || [] }}
            activeIndex={activeModule}
            onSelect={setActiveModule}
            onShowChainAudit={showChainAudit}
          />
        )}

        {chainAuditOpen && (
          <div data-testid="lipe-chain-audit-drawer" className="mb-4 p-3 rounded-lg border border-slate-300 dark:border-slate-700 bg-white/80 dark:bg-slate-900/80">
            <div className="flex justify-between items-center mb-2">
              <div className="text-xs tracking-normal text-teal-700 dark:text-teal-300">
                {t("ui.country_return.page.chain_audit", { defaultValue: "Chain audit history" })}
              </div>
              <button
                data-testid="lipe-chain-audit-close"
                onClick={() => setChainAuditOpen(false)}
                className="text-[11px] text-slate-600 dark:text-slate-300 hover:text-slate-800 dark:hover:text-slate-100"
              >
                {t("ui.country_return.page.close", { defaultValue: "close" })}
              </button>
            </div>
            {chainAuditRecords === null ? (
              <div className="text-xs text-slate-500 dark:text-slate-400">{t("ui.country_return.page.loading", { defaultValue: "loading..." })}</div>
            ) : chainAuditRecords.length === 0 ? (
              <div className="text-xs text-slate-500 dark:text-slate-400">{t("ui.country_return.page.chain_audit_empty", { defaultValue: "No chain audit records yet — bundle hasn't been evaluated." })}</div>
            ) : (
              <table className="w-full text-xs">
                <thead>
                  <tr className="text-slate-500 dark:text-slate-400">
                    <th className="text-left py-1">{t("ui.country_return.page.col_when", { defaultValue: "When" })}</th>
                    <th className="text-left py-1">{t("ui.country_return.page.col_module", { defaultValue: "Module" })}</th>
                    <th className="text-left py-1">{t("ui.country_return.page.col_carry", { defaultValue: "Carry" })}</th>
                    <th className="text-right py-1">{t("ui.country_return.page.col_value", { defaultValue: "Value" })}</th>
                  </tr>
                </thead>
                <tbody>
                  {chainAuditRecords.map((r, i) => (
                    <tr key={i} data-testid={`lipe-chain-audit-row-${i}`} className="border-t border-slate-200/70">
                      <td className="py-1 text-slate-600 dark:text-slate-300">{String(r.computed_at).slice(0, 19)}</td>
                      <td className="py-1 text-slate-900 dark:text-slate-50">M{r.module_index}</td>
                      <td className="py-1 text-slate-500 dark:text-slate-400">{r.carry_from_box} → {r.carry_to_box}</td>
                      <td className="py-1 text-slate-900 dark:text-slate-50 text-right">{r.value}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        )}

        {activePayload?.reconciliation_failures?.length > 0 && (
          <ActionRequiredBanner
            failures={activePayload.reconciliation_failures}
            calcId={calcId}
            country={upperCountry}
            clientId={livePayload?.client_id || activePayload?.client_id}
            clientCode={livePayload?.client_code || activePayload?.client_code}
            periodStart={activePayload?.period_start}
            periodEnd={activePayload?.period_end}
            savedCadence={
              livePayload?.client_local_return_frequency?.[upperCountry]
              || activePayload?.client_local_return_frequency?.[upperCountry]
            }
            onResolved={fetchPayload}
          />
        )}
        {/* v0.6.12 (A2-37) — notes that never block: amounts the form has no box for. */}
        {activePayload?.reconciliation_advisories?.length > 0 && (
          <AdvisoryNotices advisories={activePayload.reconciliation_advisories} />
        )}
        {/* v0.6.13 (A1-51) — answers raise the safe share; renders nothing without questions. */}
        {/* One table: each purchase's expense type and share (2026-10-13). key: a fresh panel per country. */}
        {isAdmin && (
          <>
            <PurchaseRows key={`${calcId}-${upperCountry}`} calcId={calcId} country={upperCountry}
                          onChanged={fetchPayload} locked={!!(livePayload?.frozen || livePayload?.signed_off)} />
            <CorrectionPanel key={`corr-${calcId}-${upperCountry}`} calcId={calcId} country={upperCountry} />
            <AuthorityCheckPanel key={`auth-${calcId}-${upperCountry}`} calcId={calcId} country={upperCountry} />
          </>
        )}
        <HeldForAnswerBanner held={activePayload?.held_for_answer} />
        <UnplacedRowsNote data={activePayload?.unplaced_rows} />
        {activePayload?.period_blocker && (   /* A2-451 */
          <div data-testid="country-return-period-blocker" role="status"
               className="border border-amber-300 dark:border-amber-700 bg-amber-50 dark:bg-amber-950/40 rounded-md px-4 py-2 text-xs text-amber-900 dark:text-amber-100">
            {t("ui.country_return.page.period_blocker", {
              reason: activePayload.period_blocker,
              defaultValue: "This is not a return you can file: {{reason}}",
            })}
          </div>
        )}
        {activePayload?.currency_blocker && (
          <div
            data-testid="currency-blocker-banner"
            className="text-sm bg-red-50 dark:bg-red-950/40 border border-l-4 border-red-500 rounded-lg p-3 mb-4 text-red-950 dark:text-red-100"
          >
            <div className="font-semibold flex items-center gap-2">
              ⚠ {t("ui.country_return.page.fx_blocker_title", { defaultValue: "Boxes read €0 — foreign-currency rows aren't converted yet" })}
            </div>
            <div className="text-[12px] opacity-90 mt-0.5 leading-snug">
              {t("ui.country_return.page.fx_blocker_before", { currency: activePayload.currency_blocker.filing_currency, defaultValue: `This return files in ${activePayload.currency_blocker.filing_currency}, but some rows are in another currency and no monthly FX rate has been entered for them. Those rows are` })} <strong>{t("ui.country_return.page.fx_blocker_excluded", { defaultValue: "excluded" })}</strong> {t("ui.country_return.page.fx_blocker_after", { defaultValue: "until you supply the rate — that is why the figures below are zero. Enter the monthly rate(s) on the Calculate step under \"Per-country national returns\", then re-run the calculation:" })}
            </div>
            <ul className="mt-2 space-y-1" data-testid="currency-blocker-list">
              {(activePayload.currency_blocker.gaps || []).map((g, i) => (
                <li key={i} className="tabular-nums text-[12px]">
                  • {t("ui.country_return.page.fx_gap_row", { currency: g.currency, filing: activePayload.currency_blocker.filing_currency, month: g.month, count: g.txn_count, defaultValue: `${g.currency} → ${activePayload.currency_blocker.filing_currency}, month ${g.month} — ${g.txn_count} row(s) missing a rate` })}
                </li>
              ))}
            </ul>
          </div>
        )}
        {summary && pendingBoxes.length > 0 && (
          <div
            data-testid="needs-input-banner"
            className="text-sm bg-amber-50 dark:bg-amber-950/40 border border-l-4 border-amber-500 rounded-lg p-3 mb-4 text-amber-950 dark:text-amber-100"
          >
            <div className="font-semibold flex items-center gap-2">
              ⚠ {t("ui.country_return.page.needs_input_title", { defaultValue: "Needs input before filing" })}
              <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-amber-600/25 tabular-nums">
                {pendingBoxes.length}
              </span>
            </div>
            <div className="text-[12px] opacity-90 mt-0.5 leading-snug">
              {t("ui.country_return.page.needs_input_body", { count: pendingBoxes.length, defaultValue: `${pendingBoxes.length} required manual-input box(es) below — enter a value (0 is fine, with a reason). Uploaded purchases fill it in once their questions are answered; without purchases the engine can't know your input VAT. Leaving it empty would silently zero your deduction.` })}
            </div>
            <ul className="mt-2 space-y-1" data-testid="needs-input-banner-list">
              {pendingBoxes.map((pb) => (
                <li key={pb.box_id}>
                  <button
                    type="button"
                    data-testid={`needs-input-goto-${pb.box_id}`}
                    onClick={() => revealBox(pb.box_id)}
                    className="text-[12px] font-semibold underline decoration-dotted underline-offset-2 hover:decoration-solid inline-flex items-center gap-1.5 text-left"
                  >
                    {/* i18n-ignore */}
                    <span className="tabular-nums px-1 py-0.5 rounded bg-amber-600/25">
                      Box {pb.box_id}
                    </span>
                    <span>{pb.label}</span>
                    <span aria-hidden>↓</span>
                  </button>
                </li>
              ))}
            </ul>
          </div>
        )}
        <CalcCheckBlocker data={activePayload?.calc_check_blocker} /><InvoicedVatNote data={activePayload?.invoiced_vat_off_return} /><PeriodCadenceNote payload={livePayload} />
        {summary && pendingBoxes.length === 0 && activePayload?.ready_to_file && (
          <div data-testid="ready-to-file-banner" className="text-sm text-teal-700 dark:text-teal-300 bg-teal-50 dark:bg-teal-900/40 border border-teal-700 rounded-lg p-3 mb-4">
            ✓ {t("ui.country_return.page.ready", { defaultValue: "All reconciliations passed. Ready to file." })}
          </div>
        )}

        {activePayload?.marketplace_facilitated && (
          <MarketplaceFooter data={activePayload.marketplace_facilitated} />
        )}

        <CopyToFormBar boxes={activePayload?.boxes} liability={activePayload?.liability} entryScale={activePayload?.entry_scale || 1} entryUnit={activePayload?.entry_unit}
          readyToFile={!!activePayload?.ready_to_file && pendingBoxes.length === 0} onlyCopy={onlyCopy} setOnlyCopy={setOnlyCopy} requiredCount={pendingBoxes.length} />
        {(() => {
          // A2-638 — which boxes are shown, folded as unused (0 this period)
          // or folded as workings not on the form; a note several boxes
          // share is shown once (components/country_return/boxView.js).
          const scale = activePayload?.entry_scale || 1;
          const allBoxes = activePayload?.boxes || [];
          // the figures to copy (not 0), the result box(es) the top names, any box in error or still owed; no box to copy = no filter
          const copyOn = onlyCopy && allBoxes.some((x) => x.portal === "enter");
          const liab = activePayload?.liability, liabIds = [liab?.box_id, liab?.reclaim?.box_id];
          const owed = (b) => pendingBoxes.some((pb) => pb.box_id === b.box_id);
          const place = (b) => boxPlace(b, { scale, keep: liabIds.includes(b.box_id) || owed(b) });
          // a note shared by several boxes is shown once; under "only the
          // figures to copy" there are no folds, so each card keeps its own
          const notes = copyOn ? [] : sharedNotes(allBoxes);
          const noteOnce = new Set(notes.flatMap((n) => n.boxes.map((b) => b.box_id)));
          const folded = (kind) => (copyOn ? [] : allBoxes.filter((b) => place(b) === kind));
          const FOLDS = ["optional", "working", "stub", "unused"];
          const inFold = Object.fromEntries(FOLDS.map((k) => [k, folded(k)]));
          // a shared note whose boxes all sit in one fold goes inside it
          const foldOfNote = (n) => FOLDS.find((k) => n.boxes.every((b) => inFold[k].some((x) => x.box_id === b.box_id)));
          const notesOnPage = notes.filter((n) => !foldOfNote(n));
          // v0.5.0d+7 — Compute whether any reconciliation failure
          // is an unknown-rate guard with active offenders. The
          // TypedInEditor for the country's manual-override box
          // uses this to warn the operator BEFORE they save that
          // direct-edit doesn't unblock the calc (the Route flow
          // does). Operators were saving Box 35 directly and
          // wondering why DE was still blocked — Route is the only
          // path that excludes the offender's txn_ids from
          // rate_split + stamps the box atomically.
          const unknownRateOffenders = (activePayload?.reconciliation_failures || [])
            .filter((f) => (f?.name || "").includes("rate_split_keys_match_known"))
            .flatMap((f) => f?.offenders || [])
            .filter((o) => (o?.txn_count || 0) > 0);
          const renderBox = (b) => (
            <Box
              key={b.box_id} box={b} calcId={calcId} country={upperCountry}
              onSaved={refresh}
              highlighted={highlightBoxId === b.box_id}
              periodStart={activePayload?.period_start}
              clientCadence={livePayload?.client_cadence}
              moduleIndex={isBundle ? activeModule + 1 : undefined}
              routeDestinationOffenders={unknownRateOffenders}
              calcFrozen={!!livePayload?.frozen}
              calcSignedOff={!!livePayload?.signed_off}
              entryScale={scale}
              entryUnit={activePayload?.entry_unit}
              required={owed(b)}
              labelLanguage={activePayload?.label_language}
              noteShownOnce={noteOnce.has(b.box_id)}
            />
          );
          return (
            <>
              {notesOnPage.length > 0 && (
                <div data-testid="shared-notes" className="mb-4 space-y-1">
                  {notesOnPage.map((n) => <SharedNote key={n.text} note={n} />)}
                </div>
              )}
              {sectionOrder.map((sec) => {
                const boxes = bySection(sec).filter((b) => place(b) === "shown"
                  && (!copyOn || isFigureToCopy(b, scale) || liabIds.includes(b.box_id) || b.error || owed(b)));
                if (!boxes.length) return null;
                const label = SECTION_LABELS[sec] || humanizeSection(sec);
                return (
                  <section key={sec} data-testid={`country-return-section-${sec}`} className="mb-6">
                    <h2 className="text-sm font-semibold text-teal-700 dark:text-teal-300 mb-2">
                      {label}
                    </h2>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                      {boxes.map(renderBox)}
                    </div>
                  </section>
                );
              })}
              {FOLDS.map((k) => (
                <FoldedBoxes key={k} kind={k} boxes={inFold[k]} open={openFold[k]}
                  notes={notes.filter((n) => foldOfNote(n) === k)}
                  onToggle={() => setOpenFold((o) => ({ ...o, [k]: !o[k] }))} renderBox={renderBox} />
              ))}
            </>
          );
        })()}
      </main>
    </div>
  );
}
